import { useState, useEffect, useCallback, useMemo } from 'react';
import { crmService } from '../../services/crmService';

// ─── Constants ─────────────────────────────────────────────────────────────────

const SOURCE_META = {
  sbir:         { label: 'SBIR Phase II', color: '#22D3EE' },
  sbir_phase1:  { label: 'SBIR Phase I',  color: '#64748B' },
  sec_edgar:    { label: 'SEC',           color: '#3B82F6' },
  usaspending:  { label: 'USASpending',   color: '#10B981' },
  manual:       { label: 'Manual',        color: '#94A3B8' },
  ingest_api:   { label: 'API',           color: '#F59E0B' },
};

const STAGE_META = {
  pre_seed:     { label: 'Pre-Seed',     color: '#F59E0B' },
  seed:         { label: 'Seed',         color: '#EAB308' },
  sbir_phase1:  { label: 'SBIR Ph.I',   color: '#64748B' },
  sbir_phase2:  { label: 'SBIR Ph.II',  color: '#22D3EE' },
  series_a:     { label: 'Series A',    color: '#3B82F6' },
  series_b:     { label: 'Series B',    color: '#2563EB' },
  series_c:     { label: 'Series C',    color: '#1D4ED8' },
  series_d_plus:{ label: 'Series D+',   color: '#1E40AF' },
  ota:          { label: 'OTA',         color: '#8B5CF6' },
  pre_ipo:      { label: 'Pre-IPO',     color: '#7C3AED' },
};

const STATUS_OPTIONS = [
  { value: 'watching',           label: 'Watching'        },
  { value: 'approaching',        label: 'Approaching'     },
  { value: 'pass',               label: 'Pass'            },
  { value: 'acquired_by_other',  label: 'Acquired (other)'},
  { value: 'converted',          label: 'Converted ✓'     },
];

const STAGE_OPTIONS = [
  { value: '',              label: 'All Stages'  },
  { value: 'sbir_phase2',   label: 'SBIR Ph.II'  },
  { value: 'sbir_phase1',   label: 'SBIR Ph.I'   },
  { value: 'seed',          label: 'Seed'        },
  { value: 'pre_seed',      label: 'Pre-Seed'    },
  { value: 'series_a',      label: 'Series A'    },
  { value: 'series_b',      label: 'Series B'    },
  { value: 'series_c',      label: 'Series C'    },
  { value: 'series_d_plus', label: 'Series D+'   },
  { value: 'ota',           label: 'OTA'         },
  { value: 'pre_ipo',       label: 'Pre-IPO'     },
];

function fmtM(v) {
  if (!v) return null;
  const n = parseFloat(v);
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}B`;
  if (n >= 1)    return `$${n.toFixed(1)}M`;
  return `$${(n * 1000).toFixed(0)}K`;
}

function getDomain(url) {
  if (!url) return null;
  try { return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, ''); }
  catch { return null; }
}

// ─── Shared styles ─────────────────────────────────────────────────────────────

const inp = {
  background: '#0A0F1C', border: '1px solid #1E2D45',
  borderRadius: 6, padding: '7px 10px', color: '#E2E8F0', fontSize: 12,
  outline: 'none', boxSizing: 'border-box',
};

function Field({ label, required, children, half }) {
  return (
    <div style={{ gridColumn: half ? 'span 1' : 'span 2', marginBottom: 2 }}>
      <label style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 600, marginBottom: 3, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

// ─── Star rating ───────────────────────────────────────────────────────────────

function Stars({ value, onChange, readonly }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1,2,3,4,5].map(n => (
        <span
          key={n}
          onClick={() => !readonly && onChange && onChange(n)}
          onMouseEnter={() => !readonly && setHover(n)}
          onMouseLeave={() => !readonly && setHover(0)}
          style={{
            fontSize: 14, cursor: readonly ? 'default' : 'pointer',
            color: n <= (hover || value) ? '#F59E0B' : '#1E2D45',
            lineHeight: 1,
          }}
        >★</span>
      ))}
    </div>
  );
}

// ─── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 4000,
      background: '#10B981', color: '#fff', fontWeight: 700, fontSize: 13,
      borderRadius: 8, padding: '10px 18px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
      animation: 'fadeIn 0.2s ease',
    }}>
      {message}
    </div>
  );
}

// ─── Update Detail Modal ──────────────────────────────────────────────────────

function UpdateDetailModal({ entry, onClose }) {
  if (!entry) return null;

  const updates = entry.updates_log || [];
  const sourcesList = entry.sources_found ? (Array.isArray(entry.sources_found) ? entry.sources_found : []) : [];
  const hasOtherSource = sourcesList.length > 1;
  const otherSource = hasOtherSource ? sourcesList.find(s => s !== entry.source) : null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#111827', border: '1px solid #1E2D45', borderRadius: 8,
        padding: 20, width: 'min(600px, 90vw)', maxHeight: '80vh', overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
          <div>
            <h3 style={{ color: '#22D3EE', margin: 0, fontSize: 16 }}>{entry.company_name}</h3>
            <p style={{ color: '#64748B', fontSize: 12, margin: '4px 0 0' }}>
              Last updated: {entry.last_synced_at ? new Date(entry.last_synced_at).toLocaleString() : 'Never'}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: 20
          }}>×</button>
        </div>

        {hasOtherSource && (
          <div style={{
            background: 'rgba(34, 211, 238, 0.1)', border: '1px solid rgba(34, 211, 238, 0.3)',
            borderRadius: 4, padding: '8px 12px', marginBottom: 16, fontSize: 12
          }}>
            <span style={{ color: '#22D3EE', fontWeight: 600 }}>Also in {otherSource === 'sec_edgar' ? 'Private Funding' : 'Public Funding'}</span>
          </div>
        )}

        {updates.length === 0 ? (
          <p style={{ color: '#64748B', fontSize: 12, textAlign: 'center', padding: 20 }}>No changes recorded</p>
        ) : (
          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: 11
          }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1E2D45' }}>
                <th style={{ padding: '8px', textAlign: 'left', color: '#94A3B8', fontWeight: 600 }}>Field</th>
                <th style={{ padding: '8px', textAlign: 'left', color: '#94A3B8', fontWeight: 600 }}>Old Value</th>
                <th style={{ padding: '8px', textAlign: 'left', color: '#94A3B8', fontWeight: 600 }}>New Value</th>
                <th style={{ padding: '8px', textAlign: 'left', color: '#94A3B8', fontWeight: 600 }}>When</th>
              </tr>
            </thead>
            <tbody>
              {updates.map((u, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #0F1419' }}>
                  <td style={{ padding: '8px', color: '#E2E8F0' }}>{u.field.replace(/_/g, ' ')}</td>
                  <td style={{ padding: '8px', color: '#94A3B8' }}>{u.old_value ?? '—'}</td>
                  <td style={{ padding: '8px', color: '#22D3EE', fontWeight: 600 }}>{u.new_value}</td>
                  <td style={{ padding: '8px', color: '#64748B', whiteSpace: 'nowrap' }}>
                    {new Date(u.updated_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <button onClick={onClose} style={{
          width: '100%', marginTop: 16, padding: '8px 16px',
          background: '#22D3EE', color: '#0A0F1C', border: 'none',
          borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: 12
        }}>Close</button>
      </div>
    </div>
  );
}

// ─── Add/Edit Modal ────────────────────────────────────────────────────────────

const BLANK = {
  company_name: '', website: '', description: '', founded_year: '',
  hq_location: '', funding_stage: '', latest_round_usd_millions: '',
  latest_round_date: '', lead_investors: '', relevance_notes: '',
  ma_potential: 3, vertical_ids: [],
};

function AddManualModal({ entry, verticals, onClose, onSaved }) {
  const isEdit = !!entry;
  const [form, setForm] = useState(() => isEdit ? {
    company_name:            entry.company_name || '',
    website:                 entry.website || '',
    description:             entry.description || '',
    founded_year:            entry.founded_year || '',
    hq_location:             entry.hq_location || '',
    funding_stage:           entry.funding_stage || '',
    latest_round_usd_millions: entry.latest_round_usd_millions || '',
    latest_round_date:       entry.latest_round_date ? entry.latest_round_date.slice(0,10) : '',
    lead_investors:          (entry.lead_investors || []).join(', '),
    relevance_notes:         entry.relevance_notes || '',
    ma_potential:            entry.ma_potential || 3,
    vertical_ids:            (entry.verticals || []).map(v => v.id),
  } : { ...BLANK });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleV = (id) => setForm(f => ({
    ...f,
    vertical_ids: f.vertical_ids.includes(id) ? f.vertical_ids.filter(x => x !== id) : [...f.vertical_ids, id],
  }));

  const handleSave = async () => {
    if (!form.company_name.trim()) { setError('Company name is required'); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        ...form,
        founded_year: form.founded_year ? parseInt(form.founded_year) : null,
        latest_round_usd_millions: form.latest_round_usd_millions ? parseFloat(form.latest_round_usd_millions) : null,
        latest_round_date: form.latest_round_date || null,
        lead_investors: form.lead_investors ? form.lead_investors.split(',').map(s => s.trim()).filter(Boolean) : null,
        source: 'manual',
      };
      if (isEdit) {
        await crmService.updateFundingWatch(entry.id, payload);
        // sync verticals
        const prev = (entry.verticals || []).map(v => v.id);
        const toAdd = payload.vertical_ids.filter(id => !prev.includes(id));
        const toDrop = prev.filter(id => !payload.vertical_ids.includes(id));
        await Promise.all([
          ...toAdd.map(id => crmService.addFundingWatchVertical(entry.id, id)),
          ...toDrop.map(id => crmService.removeFundingWatchVertical(entry.id, id)),
        ]);
      } else {
        await crmService.createFundingWatch(payload);
      }
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 10, width: Math.min(620, window.innerWidth - 32), maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #1E2D45', flexShrink: 0 }}>
          <span style={{ fontWeight: 700, color: '#F1F5F9', fontSize: 14 }}>{isEdit ? `Edit: ${entry.company_name}` : 'Add to Watch List'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0 16px' }}>
            <Field label="Company Name" required half>
              <input style={{ ...inp, width: '100%' }} value={form.company_name} onChange={e => set('company_name', e.target.value)} autoFocus />
            </Field>
            <Field label="Website" half>
              <input style={{ ...inp, width: '100%' }} value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://example.com" />
            </Field>
            <Field label="Funding Stage" half>
              <select style={{ ...inp, width: '100%' }} value={form.funding_stage} onChange={e => set('funding_stage', e.target.value)}>
                <option value="">— select —</option>
                {STAGE_OPTIONS.filter(s => s.value).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Round Size ($M)" half>
              <input style={{ ...inp, width: '100%' }} type="number" min="0" step="0.1" value={form.latest_round_usd_millions} onChange={e => set('latest_round_usd_millions', e.target.value)} placeholder="e.g. 12.5" />
            </Field>
            <Field label="Round Date" half>
              <input style={{ ...inp, width: '100%' }} type="date" value={form.latest_round_date} onChange={e => set('latest_round_date', e.target.value)} />
            </Field>
            <Field label="Founded Year" half>
              <input style={{ ...inp, width: '100%' }} type="number" min="1900" max="2030" value={form.founded_year} onChange={e => set('founded_year', e.target.value)} placeholder="e.g. 2019" />
            </Field>
            <Field label="HQ Location" half>
              <input style={{ ...inp, width: '100%' }} value={form.hq_location} onChange={e => set('hq_location', e.target.value)} placeholder="City, State" />
            </Field>
            <Field label="Lead Investors" half>
              <input style={{ ...inp, width: '100%' }} value={form.lead_investors} onChange={e => set('lead_investors', e.target.value)} placeholder="Comma-separated" />
            </Field>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 600, marginBottom: 3, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Description</label>
            <textarea style={{ ...inp, width: '100%', height: 72, resize: 'vertical', display: 'block' }} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 600, marginBottom: 3, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Relevance Notes</label>
            <textarea style={{ ...inp, width: '100%', height: 60, resize: 'vertical', display: 'block' }} value={form.relevance_notes} onChange={e => set('relevance_notes', e.target.value)} placeholder="Why is this company interesting?" />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 600, marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>M&A Potential</label>
            <Stars value={form.ma_potential} onChange={v => set('ma_potential', v)} />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 600, marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Verticals</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {verticals.map(v => {
                const on = form.vertical_ids.includes(v.id);
                return (
                  <button key={v.id} type="button" onClick={() => toggleV(v.id)} style={{
                    padding: '3px 10px', borderRadius: 10, fontSize: 10, cursor: 'pointer',
                    background: on ? '#22D3EE18' : 'transparent',
                    color: on ? '#22D3EE' : '#6B7280',
                    border: `1px solid ${on ? '#22D3EE44' : '#1E2D45'}`,
                    fontWeight: on ? 600 : 400,
                  }}>{v.name}</button>
                );
              })}
            </div>
          </div>

          {error && <div style={{ color: '#EF4444', fontSize: 12, marginTop: 12 }}>{error}</div>}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #1E2D45', display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: 'transparent', color: '#94A3B8', border: '1px solid #374151', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 6, padding: '7px 18px', cursor: 'pointer', fontSize: 12, fontWeight: 700, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add to Watch List'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Watch card ─────────────────────────────────────────────────────────────────

function WatchCard({ entry, verticals, onUpdate, onDelete, onPromote, onShowUpdateDetail }) {
  const [expanded, setExpanded]     = useState(false);
  const [promoting, setPromoting]   = useState(false);
  const [passing, setPassing]       = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [editOpen, setEditOpen]     = useState(false);

  const sourceMeta  = SOURCE_META[entry.source] || { label: entry.source || 'Unknown', color: '#64748B' };
  const stageMeta   = STAGE_META[entry.funding_stage] || null;
  const domain      = getDomain(entry.website);
  const isPassed    = entry.status === 'pass';
  const isConverted = entry.status === 'converted';

  const sourcesList = entry.sources_found ? (Array.isArray(entry.sources_found) ? entry.sources_found : []) : [];
  const hasOtherSource = sourcesList.length > 1;
  const otherSource = hasOtherSource ? sourcesList.find(s => s !== entry.source) : null;

  const handlePromote = async () => {
    if (promoting) return;
    setPromoting(true);
    try { await onPromote(entry); }
    catch (e) { alert(e.message); }
    finally { setPromoting(false); }
  };

  const handlePass = async () => {
    setPassing(true);
    try { await onUpdate(entry.id, { status: 'pass' }); }
    catch (e) { alert(e.message); }
    finally { setPassing(false); }
  };

  const handleStarChange = async (n) => {
    try { await onUpdate(entry.id, { ma_potential: n }); }
    catch (e) { console.error(e); }
  };

  const handleStatusChange = async (e) => {
    try { await onUpdate(entry.id, { status: e.target.value }); }
    catch (err) { console.error(err); }
  };

  const desc = entry.description || '';
  const descLong = desc.length > 200;

  return (
    <>
      <div style={{
        background: '#111827', border: '1px solid #1E2D45', borderRadius: 10,
        padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
        opacity: isPassed ? 0.5 : 1, transition: 'opacity 0.2s',
        position: 'relative',
      }}>
        {/* Top row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ color: '#F1F5F9', fontWeight: 700, fontSize: 14 }}>{entry.company_name}</span>
              {domain && (
                <a href={entry.website} target="_blank" rel="noopener noreferrer" title={domain}
                  onClick={e => e.stopPropagation()}
                  style={{ color: '#22D3EE', fontSize: 11, textDecoration: 'none' }}>
                  ↗
                </a>
              )}
              {isConverted && (
                <span style={{ background: '#10B98122', color: '#10B981', border: '1px solid #10B98144', borderRadius: 4, padding: '1px 6px', fontSize: 9, fontWeight: 700 }}>
                  IN M&A PIPELINE
                </span>
              )}
            </div>
            {entry.hq_location && (
              <div style={{ color: '#4B5563', fontSize: 11, marginTop: 2 }}>{entry.hq_location}</div>
            )}
          </div>
          {/* Edit/delete */}
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button onClick={() => setEditOpen(true)} style={{ background: 'none', border: '1px solid #1E2D45', borderRadius: 4, color: '#64748B', cursor: 'pointer', padding: '2px 7px', fontSize: 11 }}>✏</button>
            <button
              onClick={() => { if (!confirmDel) { setConfirmDel(true); return; } onDelete(entry.id); }}
              onBlur={() => setTimeout(() => setConfirmDel(false), 200)}
              style={{ background: confirmDel ? '#EF444422' : 'none', border: `1px solid ${confirmDel ? '#EF4444' : '#1E2D45'}`, borderRadius: 4, color: confirmDel ? '#EF4444' : '#64748B', cursor: 'pointer', padding: '2px 7px', fontSize: 11 }}
            >
              {confirmDel ? '?' : '✕'}
            </button>
          </div>
        </div>

        {/* Badges row */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ background: sourceMeta.color + '22', color: sourceMeta.color, border: `1px solid ${sourceMeta.color}44`, borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>
            {sourceMeta.label}
          </span>
          {stageMeta && (
            <span style={{ background: stageMeta.color + '22', color: stageMeta.color, border: `1px solid ${stageMeta.color}44`, borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>
              {stageMeta.label}
            </span>
          )}
          {entry.latest_round_usd_millions && (
            <span style={{ color: '#22D3EE', fontWeight: 700, fontSize: 12 }}>
              {fmtM(entry.latest_round_usd_millions)}
            </span>
          )}
          {entry.latest_round_date && (
            <span style={{ color: '#4B5563', fontSize: 10 }}>
              {new Date(entry.latest_round_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
            </span>
          )}
          {entry.is_updated_today && onShowUpdateDetail && (
            <button
              onClick={() => onShowUpdateDetail(entry)}
              style={{
                background: 'rgba(34, 211, 238, 0.2)',
                color: '#22D3EE',
                border: '1px solid rgba(34, 211, 238, 0.4)',
                borderRadius: 4,
                padding: '2px 8px',
                fontSize: 10,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Updated today
            </button>
          )}
          {otherSource && (
            <span style={{
              background: 'rgba(168, 85, 247, 0.15)',
              color: '#A855F7',
              border: '1px solid rgba(168, 85, 247, 0.3)',
              borderRadius: 4,
              padding: '2px 8px',
              fontSize: 9,
              fontWeight: 600,
            }}>
              Also in {otherSource === 'sec_edgar' ? 'Private Funding' : 'Public Funding'}
            </span>
          )}
        </div>

        {/* Verticals */}
        {entry.verticals?.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {entry.verticals.slice(0, 3).map(v => (
              <span key={v.id} style={{ background: '#22D3EE0F', color: '#22D3EE', border: '1px solid #22D3EE25', borderRadius: 8, padding: '1px 7px', fontSize: 9, fontWeight: 600 }}>
                {v.name}
              </span>
            ))}
            {entry.verticals.length > 3 && (
              <span style={{ color: '#4B5563', fontSize: 10, alignSelf: 'center' }}>+{entry.verticals.length - 3}</span>
            )}
          </div>
        )}

        {/* Description */}
        {desc && (
          <div>
            <div
              onClick={() => descLong && setExpanded(e => !e)}
              style={{
                color: '#94A3B8', fontSize: 12, lineHeight: 1.6,
                overflow: 'hidden', display: '-webkit-box',
                WebkitLineClamp: expanded ? 'unset' : 3,
                WebkitBoxOrient: 'vertical',
                cursor: descLong ? 'pointer' : 'default',
              }}
            >{desc}</div>
            {descLong && (
              <button onClick={() => setExpanded(e => !e)} style={{ background: 'none', border: 'none', color: '#22D3EE', fontSize: 10, cursor: 'pointer', padding: 0, marginTop: 2 }}>
                {expanded ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>
        )}

        {/* Relevance notes */}
        {entry.relevance_notes && (
          <div style={{ color: '#6B7280', fontSize: 11, fontStyle: 'italic', lineHeight: 1.5 }}>
            {entry.relevance_notes}
          </div>
        )}

        {/* Source URL */}
        {entry.source_url && (
          <a href={entry.source_url} target="_blank" rel="noopener noreferrer"
            style={{ color: '#22D3EE', fontSize: 11, textDecoration: 'none' }}>
            View Source Award →
          </a>
        )}

        {/* M&A Potential + Status */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 4, borderTop: '1px solid #0D1117' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#4B5563', fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>M&A</span>
            <Stars value={entry.ma_potential} onChange={handleStarChange} />
          </div>
          <select
            value={entry.status}
            onChange={handleStatusChange}
            onClick={e => e.stopPropagation()}
            style={{ ...inp, fontSize: 10, padding: '3px 7px', width: 'auto', cursor: 'pointer' }}
          >
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        {/* Action buttons */}
        {!isConverted && !isPassed && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handlePromote}
              disabled={promoting}
              style={{
                flex: 1, background: '#22D3EE', color: '#0A0F1C', border: 'none',
                borderRadius: 6, padding: '7px 0', cursor: promoting ? 'default' : 'pointer',
                fontSize: 11, fontWeight: 700, opacity: promoting ? 0.7 : 1,
              }}
            >
              {promoting ? 'Promoting…' : '⬆ Promote to M&A'}
            </button>
            <button
              onClick={handlePass}
              disabled={passing}
              style={{
                background: 'transparent', color: '#EF4444',
                border: '1px solid #EF444444', borderRadius: 6,
                padding: '7px 14px', cursor: 'pointer', fontSize: 11,
                opacity: passing ? 0.7 : 1,
              }}
            >
              Pass
            </button>
          </div>
        )}
      </div>

      {editOpen && (
        <AddManualModal
          entry={entry}
          verticals={verticals}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); onUpdate(entry.id, {}); }}
        />
      )}
    </>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────

export default function FundingWatch() {
  const [entries, setEntries]     = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [syncing, setSyncing]     = useState(false);
  const [syncingSEC, setSyncingSEC] = useState(false);
  const [addOpen, setAddOpen]     = useState(false);
  const [toast, setToast]         = useState(null);
  const [showUpdateDetail, setShowUpdateDetail] = useState(null); // Entry with updates to show

  // Filters (client-side)
  const [search, setSearch]         = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [verticalFilter, setVertF]  = useState('');
  const [statusFilter, setStatusF]  = useState('watching');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, verts] = await Promise.all([
        crmService.getFundingWatch(),
        crmService.getVerticals(),
      ]);
      setEntries(data);
      setVerticals(verts);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return entries.filter(e => {
      if (statusFilter && statusFilter !== 'all' && e.status !== statusFilter) return false;
      if (stageFilter && e.funding_stage !== stageFilter) return false;
      if (verticalFilter && !(e.verticals || []).some(v => v.id === parseInt(verticalFilter))) return false;
      if (q && !e.company_name.toLowerCase().includes(q) && !(e.description || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [entries, search, stageFilter, verticalFilter, statusFilter]);

  // Summary counts (from full entries list, not filtered)
  const counts = useMemo(() => ({
    watching:   entries.filter(e => e.status === 'watching').length,
    sbir:       entries.filter(e => e.funding_stage === 'sbir_phase2').length,
    seed:       entries.filter(e => ['seed','pre_seed'].includes(e.funding_stage)).length,
    approaching:entries.filter(e => e.status === 'approaching').length,
    converted:  entries.filter(e => e.status === 'converted').length,
  }), [entries]);

  const handleUpdate = useCallback(async (id, patch) => {
    try {
      if (Object.keys(patch).length > 0) await crmService.updateFundingWatch(id, patch);
      await load();
    } catch (e) { throw e; }
  }, [load]);

  const handleDelete = useCallback(async (id) => {
    try { await crmService.deleteFundingWatch(id); load(); }
    catch (e) { alert(e.message); }
  }, [load]);

  const handlePromote = useCallback(async (entry) => {
    await crmService.promoteFundingWatch(entry.id);
    await load();
    setToast(`Added "${entry.company_name}" to M&A pipeline`);
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await crmService.syncFundingWatchNow();
      setToast('Deal flow sync started — awards land over the next ~30 seconds');
      setTimeout(load, 5000);  // early partial results
      setTimeout(load, 30000); // full sync (12 verticals, rate-limited)
    } catch (e) { alert(e.message); }
    finally { setSyncing(false); }
  };

  const handleSyncSEC = async () => {
    setSyncingSEC(true);
    try {
      const result = await crmService.syncSECEDGAR();
      setToast(`✅ SEC EDGAR: ${result.companies_added} new companies added`);
      setTimeout(load, 2000);
    } catch (e) { alert(e.message); }
    finally { setSyncingSEC(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Filter bar */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #1E2D45', background: '#111827', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search company…"
          style={{ ...inp, width: 180 }}
        />
        <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
          {STAGE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={verticalFilter} onChange={e => setVertF(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
          <option value="">All Verticals</option>
          {verticals.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusF(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
          <option value="watching">Watching</option>
          <option value="approaching">Approaching</option>
          <option value="all">All Status</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            onClick={handleSync} disabled={syncing}
            title="Sync public funding from USASpending"
            style={{ background: '#0D1F3C', color: '#22D3EE', border: '1px solid #22D3EE44', borderRadius: 6, padding: '6px 12px', cursor: syncing ? 'default' : 'pointer', fontSize: 11, fontWeight: 600, opacity: syncing ? 0.7 : 1 }}
          >
            {syncing ? 'Syncing…' : 'Sync Public Funding'}
          </button>
          <button
            onClick={handleSyncSEC} disabled={syncingSEC}
            title="Sync private funding from SEC EDGAR Form D"
            style={{ background: '#0D1F3C', color: '#3B82F6', border: '1px solid #3B82F644', borderRadius: 6, padding: '6px 12px', cursor: syncingSEC ? 'default' : 'pointer', fontSize: 11, fontWeight: 600, opacity: syncingSEC ? 0.7 : 1 }}
          >
            {syncingSEC ? 'Syncing…' : 'Sync Private Funding'}
          </button>
          <button
            onClick={() => setAddOpen(true)}
            style={{ background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}
          >
            + Add Manual
          </button>
        </div>
      </div>

      {/* Summary bar */}
      <div style={{ padding: '6px 16px', background: '#0A0F1C', borderBottom: '1px solid #1E2D45', flexShrink: 0, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          [`${counts.watching} watching`,     '#F1F5F9'],
          [`${counts.sbir} SBIR Phase II`,    '#22D3EE'],
          [`${counts.seed} seed/pre-seed`,    '#F59E0B'],
          [`${counts.approaching} approaching`,'#10B981'],
          [`${counts.converted} converted`,   '#8B5CF6'],
        ].map(([label, color]) => (
          <span key={label} style={{ fontSize: 11, color }}>
            {label}
          </span>
        ))}
        {filtered.length !== entries.length && (
          <span style={{ fontSize: 11, color: '#4B5563', marginLeft: 'auto' }}>
            Showing {filtered.length} of {entries.length}
          </span>
        )}
      </div>

      {/* Card grid */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {loading ? (
          <div style={{ color: '#64748B', textAlign: 'center', padding: 40 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ color: '#4B5563', textAlign: 'center', padding: 40 }}>
            {entries.length === 0 ? 'No companies in the watch list. Add one manually or sync SBIR.' : 'No companies match the current filters.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12, alignItems: 'start' }}>
            {filtered.map(entry => (
              <WatchCard
                key={entry.id}
                entry={entry}
                verticals={verticals}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onPromote={handlePromote}
                onShowUpdateDetail={setShowUpdateDetail}
              />
            ))}
          </div>
        )}
      </div>

      {addOpen && (
        <AddManualModal
          entry={null}
          verticals={verticals}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load(); }}
        />
      )}

      {showUpdateDetail && (
        <UpdateDetailModal
          entry={showUpdateDetail}
          onClose={() => setShowUpdateDetail(null)}
        />
      )}

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}
